<template>
  <ProductList :items="displayedProducts" :size="size">
    <template #card-footer="productCardProps">
      <ProductCardFooter
        :has-fast-delivery="productIdsByDelivery[productCardProps.product.id]"
        :product-card-props="productCardProps"
      />
    </template>
  </ProductList>
  <RelatedProductsSkeleton v-if="isLoading || !isSkeletonTimeOutPassed" />
  <Spacer spacing-grid="xsmall" />
  <ButtonGroupBlock
    v-if="displayButton"
    :content="[
      {
        text: $textDictionary['pdp.relatedproducts.button.more.label'],
        link: 'javascript: void(0)',
        styleType: 'primary'
      } as Link
    ]"
    :cols="8"
    :cols-centered="true"
    :content-centered="true"
    @click="loadMore"
  />
</template>

<script setup lang="ts">
import {
  ButtonGroupBlock,
  ProductList,
  Spacer
} from '@hypercodestudio/basler-components';

import type { Link } from '@hypercodestudio/basler-components/dist/components/content-modules/blocks/ButtonGroupBlock.vue';
import ProductCardFooter from './ProductCardFooter.vue';
import RelatedProductsSkeleton from './skeletons/RelatedProductsSkeleton.vue';
import type {
  CommonProductDetailFragment,
  CommonProductFragment
} from '~/lib/Shop/generated/schema';
import { useCreateProductDetailLink } from '~/composables/useCreateProductDetailLink';
import { isDefined } from '~/utils/guards/isDefined';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import type { RelatedProductGroupModel } from '~/utils/shop/DecoratedProduct';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';

type Sizes = 'threequaters' | 'full';

type Props = {
  group: RelatedProductGroupModel;
  size: Sizes;
  generateDetailLink: (p: DecoratedProduct) => string;
};

const props = defineProps<Props>();
const { $textDictionary, $locale, $shopStore, $globalPageSettings } =
  useNuxtApp();

const isLoading = ref(false);
const isSkeletonTimeOutPassed = ref(false);
const skeletonTimeout = ref<NodeJS.Timeout>();
const error = ref<Error | null>(null);
const abortController = ref<AbortController | undefined>(undefined);
const stackSize = ref(4);
const buttonPressed = ref(1);
const products = ref<(CommonProductFragment | CommonProductDetailFragment)[]>(
  []
);
const lastResponseProducts = ref<
  (CommonProductFragment | CommonProductDetailFragment)[]
>([]);
const lastResponseTotalCount = ref<number | null>(null);

const skus = ref<string[]>(
  props.group.products.map((p) => p.sku).filter(isDefined)
);

onMounted(() => {
  skeletonTimeout.value = setTimeout(
    () => (isSkeletonTimeOutPassed.value = true),
    250
  );
});

const doRequest = async () => {
  if (!skus.value.length) {
    // nothing to do
    return;
  }

  if (!abortController.value?.signal.aborted) {
    abortController.value?.abort();
  }

  const currentAbortController = new AbortController();
  abortController.value = currentAbortController;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await $fetch<{
      items: (CommonProductFragment | CommonProductDetailFragment)[];
      total_count: number;
    }>('/api/magento/productsBySkus', {
      ...DEFAULT_FETCH_OPTIONS,
      query: {
        store: $shopStore.value,
        locale: $locale.value,
        skus: skus.value,
        pageSize: stackSize.value,
        cursor: buttonPressed.value,
        queryFilter: props.group.queryFilter || {}
      },
      signal: currentAbortController.signal
    });

    lastResponseProducts.value = response?.items;
    lastResponseTotalCount.value = response?.total_count;

    products.value = [...products.value, ...lastResponseProducts.value];
    error.value = null;
  } catch (e: unknown) {
    // ignore abort exceptions
    if (currentAbortController.signal.aborted) {
      return;
    }
    error.value = e as Error;
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async () => {
  buttonPressed.value++;
  await doRequest();
};

const createProductDetailLink = useCreateProductDetailLink();

const pdpConfiguration = usePdpConfiguration();
const displayedProducts = computed(() => {
  const configuration = pdpConfiguration.value;
  if (!configuration) {
    return [];
  }

  return products.value.map((x) =>
    DecoratedProduct.build(x, $textDictionary.value, $locale.value).asListEntry(
      configuration,
      {},
      createProductDetailLink,
      undefined,
      undefined,
      $globalPageSettings.value?.defaultProductImage ?? undefined
    )
  );
});

type ProductByDelivery = Record<string, boolean>;

const productIdsByDelivery = computed<ProductByDelivery>(() => {
  return products.value.reduce((prev, product) => {
    const decoratedProduct = DecoratedProduct.build(product);
    return { ...prev, [decoratedProduct.id]: decoratedProduct.hasFastDelivery };
  }, {});
});

const displayButton = computed(() => {
  if (isLoading.value) {
    return false;
  }
  if (isDefined(lastResponseTotalCount.value)) {
    return lastResponseTotalCount.value > stackSize.value * buttonPressed.value;
  }
  return false;
});

watch(
  () => props.group.products,
  () => {
    const newSkus = props.group.products.map((p) => p.sku).filter(isDefined);
    if (
      skus.value.length !== newSkus.length ||
      !skus.value.every((sku) => newSkus.includes(sku))
    ) {
      skus.value = newSkus;
    }
  }
);

watch(skus, () => {
  // reset products once groups change
  products.value = [];
  buttonPressed.value = 0;
});

watch(
  skus,
  () => {
    doRequest();
  },
  { immediate: true }
);
</script>
